import acceptLanguage from "accept-language";
import { getCultures } from "@/features/shared/api/get-cultures";
import { getCookie, setCookie } from "@/lib/cookies";
import { fallbackLng, languages } from "@/lib/i18n/settings";
import { useConfigStore } from "@/store/use-config-store";
import { getCulture } from "@/utils/getCulture";

const APP_BASE = "/membersapp";
const cookieName = "i18next";
const shopCultureCookie = "shop-culture";

const IIS_PIPE_SEGMENT =
  /\/?pipe\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}(?=\/|$)/gi;

type ShopCultureState = {
  shortCodes: string[];
  shopCultureByShort: Record<string, string>;
};

function defaultCultureState(): ShopCultureState {
  return {
    shortCodes: [...languages],
    shopCultureByShort: Object.fromEntries(languages.map((s) => [s, getCulture(s)]))
  };
}

let shopCultures = defaultCultureState();
let ensureShopCulturesPromise: Promise<void> | null = null;

function applyAcceptLanguage(state: ShopCultureState) {
  acceptLanguage.languages([...new Set([...state.shortCodes, ...Object.values(state.shopCultureByShort)])]);
}

applyAcceptLanguage(shopCultures);

export function normalizePathname(pathname: string): string {
  if (!pathname) return "/";
  let p = pathname.replace(IIS_PIPE_SEGMENT, "/");
  p = p.replace(/\/{2,}/g, "/");
  if (!p.startsWith("/")) p = `/${p}`;
  if (p === APP_BASE || p.startsWith(`${APP_BASE}/`)) {
    p = p.slice(APP_BASE.length) || "/";
  }
  p = p.replace(/^(\/[^/]+)\/membersapp(?=\/|$)/, "$1");
  p = p.replace(/\/{2,}/g, "/");
  return p || "/";
}

function pathStartsWithLocale(segments: string[], shortCodes: string[]): boolean {
  const first = segments[0]?.toLowerCase();
  if (!first) return false;
  return shortCodes.some((loc) => {
    const l = loc.toLowerCase();
    return l === first || l.split("-")[0] === first;
  });
}

function toShortLocale(negotiated: string, shortCodes: string[]) {
  const lower = negotiated.toLowerCase();
  return (
    shortCodes.find((l) => l === lower || lower.startsWith(`${l}-`)) ??
    shortCodes.find((l) => negotiated.split("-")[0]?.toLowerCase() === l) ??
    fallbackLng
  );
}

function setLocaleCookies(shortLng: string, map: Record<string, string>) {
  const key = shortLng.toLowerCase();
  const full = map[key] ?? getCulture(shortLng);
  setCookie(cookieName, full, { path: "/", sameSite: "lax" });
  setCookie(shopCultureCookie, full, { path: "/", sameSite: "lax" });
}

export type LocaleBootstrapResult =
  | { type: "redirect"; to: string }
  | { type: "continue" };

/**
 * One-time shop cultures fetch for the session. Call after config is loaded —
 * never from route beforeLoad (intent preload re-runs those).
 */
export function ensureShopCultures(): Promise<void> {
  const shopApiUri = useConfigStore.getState().settings?.shopApiUri;
  if (!shopApiUri) return Promise.resolve();
  if (ensureShopCulturesPromise) return ensureShopCulturesPromise;

  ensureShopCulturesPromise = (async () => {
    try {
      const cultureRoot = await getCultures();
      const nextShort: string[] = [];
      const nextMap: Record<string, string> = {};
      for (const { name } of cultureRoot.cultures ?? []) {
        if (!name) continue;
        const full = String(name);
        const short = full.split("-")[0]?.toLowerCase();
        if (!short || nextMap[short]) continue;
        nextMap[short] = full;
        nextShort.push(short);
      }
      if (nextShort.length > 0) {
        shopCultures = { shortCodes: nextShort, shopCultureByShort: nextMap };
        applyAcceptLanguage(shopCultures);
      }
    } catch {
      // keep static language fallback
    }
  })();

  return ensureShopCulturesPromise;
}

/**
 * Sync locale path/cookie resolution for root beforeLoad.
 * Must stay network-free — TanStack intent preload re-runs beforeLoad on hover.
 */
export function bootstrapLocale(pathname: string): LocaleBootstrapResult {
  const { shortCodes, shopCultureByShort } = shopCultures;

  const path = normalizePathname(pathname);
  const segments = path.split("/").filter(Boolean);
  const hasPathLocale = pathStartsWithLocale(segments, shortCodes);

  // Old bookmarks: /sv/account → /account (sync cookies from the stripped segment)
  if (hasPathLocale) {
    const first = segments[0]?.toLowerCase() ?? fallbackLng;
    setLocaleCookies(first, shopCultureByShort);
    const rest = segments.slice(1).join("/");
    return { type: "redirect", to: rest ? `/${rest}` : "/" };
  }

  // Ensure culture cookies exist (no URL change)
  if (!getCookie(shopCultureCookie)) {
    let lng: string | undefined;
    const i18nCookie = getCookie(cookieName);
    if (i18nCookie) {
      lng = acceptLanguage.get(String(i18nCookie)) ?? undefined;
    }
    if (!lng && typeof navigator !== "undefined") {
      lng = acceptLanguage.get(navigator.language) ?? undefined;
    }
    if (!lng) lng = fallbackLng;
    setLocaleCookies(toShortLocale(lng, shortCodes), shopCultureByShort);
  }

  return { type: "continue" };
}
